import React, {
    FC,
    MutableRefObject,
    ReactElement,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    CmeListLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getCmeList } from '../../../../React/Common/API/ItemsGetters'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { CmeListItem, CmeListResponse, CmeListRow } from '../../../../NonReact/Types/CmeListTypes'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { deleteCMEMany } from '../../../../NonReact/common/API/EntityAPIClient'
import CMEModal from '../../../../React/CMEAddModify/CMEModal'
import { ViewOnlyContextProvider } from '../../../../React/Common/Contexts/ViewOnlyContext'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import {
    ADMIN_URL_QUERY_CME,
    ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN,
    AdminListUrlBootstrap,
    buildAdminListPathWithQuery,
    parseAdminListUrlSearchParams
} from '../../../Utils/adminListUrlParams'
import { AdminListCountLink } from '../../Atoms/AdminListCountLink/AdminListCountLink'
import { RouteID } from '../../Molecules/Header/Types/Types'

/**
 * Routed CM Environments list view.
 *
 * @returns CM Environments list page content.
 */
export const CmeList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(CmeListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [cmeRows, setCmeRows]: ReactState<CmeListRow[]> = useState<CmeListRow[]>([])
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isCmeModalOpen, setCmeModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedCmeId, setSelectedCmeId]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [cmeModalViewOnly, setCmeModalViewOnly]: ReactState<boolean> = useState<boolean>(false)

    const filterCMEnvironmentIDForModal: string = String(userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID)

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad

    const listSearch: string = filters?.search ?? EMPTY_STRING

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_CMENVIRONMENTS, filters, userInfo?.Scope?.CMEnvironmentID)

    /**
     * Fetches CM Environment rows for the current filter and pagination state.
     *
     * @returns No return value.
     */
    const loadCmeList = useCallback((): void => {
        if (isBootstrappingFromUrl) return

        setLoading((n: number): number => n + 1)
        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getCmeList(currentPage * rowsPerPage, rowsPerPage, sort, searchQuery)
            .then((response: Nullable<CmeListResponse>): void => {
                if (!isMounted.current) return

                const items: CmeListItem[] = response?.Data?.Items ?? []
                const rows: CmeListRow[] = items.map(
                    (item: CmeListItem, index: number): CmeListRow => ({
                        key: item.CMEnvironmentID ?? `cme-row-${index}`,
                        ID: item.CMEnvironmentID ?? EMPTY_STRING,
                        Description: item.Description ?? EMPTY_STRING,
                        ListChildCMEnvironmentsCount: item.ListChildCMEnvironmentsCount,
                        ListTelSyssCount: item.ListTelSyssCount,
                        ListPartitionsCount: item.ListPartitionsCount,
                        ListUsersCount: item.ListUsersCount
                    })
                )
                setCmeRows(rows)
                setTotalCount(response?.Data?.Total ?? 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)

                if (isMounted.current) setLoading((n: number): number => n - 1)
            })
    }, [isBootstrappingFromUrl, listSearch, pagination, sort, setLoading, setTotalCount])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect((): void => {
        loadCmeList()
    }, [loadCmeList])

    const refreshCmeList = useCallback((): void => {
        loadCmeList()
    }, [loadCmeList])

    const columns: DataTableColumn<CmeListRow, keyof CmeListRow>[] = useMemo(
        (): DataTableColumn<CmeListRow, keyof CmeListRow>[] => [
            {
                headerName: language.get(CmeListLanguage.CmeList_Column_Description),
                field: 'Description',
                key: 'cme-list-description',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(CmeListLanguage.CmeList_Column_ChildCMEs),
                field: 'ListChildCMEnvironmentsCount',
                key: 'cme-list-child-cmes',
                sortable: false,
                hideBelow: 'sm',
                renderCell: (row: CmeListRow): string => row.ListChildCMEnvironmentsCount ?? EMPTY_STRING
            },
            {
                headerName: language.get(CmeListLanguage.CmeList_Column_TelSyss),
                field: 'ListTelSyssCount',
                key: 'cme-list-telsys',
                sortable: false,
                hideBelow: 'sm',
                disableRowSelection: true,
                renderCell: (row: CmeListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListTelSyssCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.TELSYS}`, {
                            [ADMIN_URL_QUERY_CME]: row.ID,
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: 'true'
                        })}
                        ariaLabel={language.get(CmeListLanguage.CmeList_CountLink_TelSyssAria)}
                    />
                )
            },
            {
                headerName: language.get(CmeListLanguage.CmeList_Column_Partitions),
                field: 'ListPartitionsCount',
                key: 'cme-list-partitions',
                sortable: false,
                hideBelow: 'sm',
                renderCell: (row: CmeListRow): string => row.ListPartitionsCount ?? EMPTY_STRING
            },
            {
                headerName: language.get(CmeListLanguage.CmeList_Column_Users),
                field: 'ListUsersCount',
                key: 'cme-list-users',
                sortable: false,
                hideBelow: 'sm',
                disableRowSelection: true,
                renderCell: (row: CmeListRow): ReactNode => (
                    <AdminListCountLink
                        count={row.ListUsersCount}
                        to={buildAdminListPathWithQuery(`/${RouteID.USERS}`, {
                            [ADMIN_URL_QUERY_CME]: row.ID,
                            [ADMIN_URL_QUERY_INCLUDE_CME_CHILDREN]: 'true'
                        })}
                        ariaLabel={language.get(CmeListLanguage.CmeList_CountLink_UsersAria)}
                    />
                )
            }
        ],
        [language]
    )

    return (
        <div className='cme-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_cme}></PageTitle>
            <div className='cme-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    onChange={(values: AdminFilterValues): void => setFilters(values)}
                    optionsToDisplay={{
                        cme: false,
                        telephonyServer: false,
                        partition: false,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: false,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={loadCmeList}
                    urlBootstrap={urlBootstrap}
                />
                <DataTable<CmeListRow, keyof CmeListRow>
                    permissionName={AdminBackendPermissionNames.CME}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={cmeRows}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => {
                        setSelectedCmeId(undefined)
                        setCmeModalViewOnly(false)
                        setCmeModalOpen(true)
                    }}
                    onToolbarChangeClick={([row]: CmeListRow[]): void => {
                        if (!row || !isStringNotEmpty(row.ID)) return

                        setSelectedCmeId(row.ID)
                        setCmeModalViewOnly(false)
                        setCmeModalOpen(true)
                    }}
                    onToolbarViewClick={([row]: CmeListRow[]): void => {
                        if (!row || !isStringNotEmpty(row.ID)) return

                        setSelectedCmeId(row.ID)
                        setCmeModalViewOnly(true)
                        setCmeModalOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: CmeListRow[]): void => {
                        setLoading((n: number): number => n + 1)
                        deleteCMEMany(selectedItems.map((row: CmeListRow): string => row.ID))
                            .then((): void => {
                                loadCmeList()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current) setLoading((n: number): number => n - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isCmeModalOpen ? (
                    <ViewOnlyContextProvider initialValue={cmeModalViewOnly}>
                        <CMEModal
                            key={`${selectedCmeId ?? 'new'}-${cmeModalViewOnly ? 'v' : 'e'}`}
                            filterCMEnvironmentID={filterCMEnvironmentIDForModal}
                            refresh={refreshCmeList}
                            cmeID={selectedCmeId}
                            onModalExited={(): void => {
                                setCmeModalOpen(false)
                                setSelectedCmeId(undefined)
                            }}
                        />
                    </ViewOnlyContextProvider>
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
